import { useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { Home, Heart, Info, Phone, CreditCard, MessageSquare, LogIn, LogOut, Menu, X, Megaphone } from "lucide-react";
import { useResident } from "../../context/ResidentAuth";

const TABS = [
  { to: "/", label: "Ana Sayfa", icon: Home, id: "home" },
  { to: "/duyurular", label: "Duyurular", icon: Megaphone, id: "announcements" },
  { to: "/tebrik-taziye", label: "Tebrik & Taziye", icon: Heart, id: "community" },
  { to: "/sitemiz", label: "Sitemiz", icon: Info, id: "about" },
  { to: "/iletisim", label: "İletişim", icon: Phone, id: "contact" },
  { to: "/odemeler", label: "Ödemeler", icon: CreditCard, id: "payments", auth: true },
  { to: "/dilek-sikayet", label: "Dilek & Şikayet", icon: MessageSquare, id: "feedback", auth: true },
];

const linkCls = ({ isActive }) =>
  `flex items-center gap-1.5 px-2.5 py-2 rounded-full text-sm whitespace-nowrap font-semibold transition-colors duration-200 ${
    isActive ? "bg-[#1B4332] text-white" : "text-slate-600 hover:bg-[#D8F3DC] hover:text-[#1B4332]"
  }`;

export default function SiteHeader() {
  const { resident, logout } = useResident();
  const [open, setOpen] = useState(false);
  const nav = useNavigate();
  const tabs = TABS.filter((t) => !t.auth || resident);

  const doLogout = () => {
    logout();
    setOpen(false);
    nav("/");
  };

  const AuthBtn = ({ mobile }) =>
    resident ? (
      <button onClick={doLogout} data-testid={mobile ? "mobile-logout-btn" : "logout-btn"}
        className="flex items-center justify-center gap-2 px-4 sm:px-5 py-2.5 rounded-full text-sm font-bold whitespace-nowrap bg-slate-100 text-slate-700 hover:bg-slate-200 transition-colors">
        <LogOut size={16} /> Çıkış
      </button>
    ) : (
      <Link to="/giris" onClick={() => setOpen(false)} data-testid={mobile ? "mobile-login-btn" : "login-btn"}
        className="flex items-center justify-center gap-2 px-4 sm:px-5 py-2.5 rounded-full text-sm font-bold whitespace-nowrap bg-[#E07A5F] text-white hover:bg-[#c9664c] transition-colors">
        <LogIn size={16} /> Giriş Yap
      </Link>
    );

  return (
    <header className="sticky top-0 z-50 backdrop-blur-xl bg-[#FDFBF7]/80 border-b border-black/5">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-20 flex items-center justify-between gap-4">
        <Link to="/" className="flex items-center gap-3 shrink-0" data-testid="site-logo">
          <img src="/logo.jpg" alt="Başkent Sitesi logosu" className="w-14 h-14 rounded-full object-cover border-2 border-white shadow-sm" data-testid="site-logo-image" />
          <span className="leading-none">
            <span className="block font-display text-xl font-extrabold tracking-tight text-[#1B4332]">BAŞKENT</span>
            <span className="eyebrow text-[#E07A5F]">Sitesi · Akyurt</span>
          </span>
        </Link>
        <nav className="hidden xl:flex items-center gap-0.5">
          {tabs.map((t) => (
            <NavLink key={t.id} to={t.to} end={t.to === "/"} className={linkCls} data-testid={`nav-${t.id}`}>
              <t.icon size={16} /> {t.label}
            </NavLink>
          ))}
        </nav>
        <div className="flex items-center gap-2 sm:gap-3 ml-auto xl:ml-0">
          {resident && (
            <span className="hidden 2xl:block text-right leading-tight whitespace-nowrap" data-testid="header-resident-name">
              <span className="block text-sm font-bold text-[#1B4332]">{resident.name}</span>
              <span className="text-xs text-slate-500">{resident.villa_no} · {resident.type}</span>
            </span>
          )}
          <AuthBtn />
        </div>
        <button className="xl:hidden p-2 -mr-2 text-slate-700" onClick={() => setOpen(!open)} data-testid="mobile-menu-toggle" aria-label="Menü">
          {open ? <X size={26} /> : <Menu size={26} />}
        </button>
      </div>
      {open && (
        <div className="xl:hidden border-t border-black/5 bg-[#FDFBF7] px-4 pb-6 pt-3 flex flex-col gap-1 shadow-xl" data-testid="mobile-menu">
          {tabs.map((t) => (
            <NavLink key={t.id} to={t.to} end={t.to === "/"} onClick={() => setOpen(false)} className={linkCls} data-testid={`mobile-nav-${t.id}`}>
              <t.icon size={18} /> {t.label}
            </NavLink>
          ))}
        </div>
      )}
    </header>
  );
}
